import './navbar.less'
import { findSelectedKey } from './util'
import { RootState } from '../../rootReducer'
import { ProtectedContent } from '../../HOCs/ProtectedContent'
import apiService from '../../services/api/apiService'
import '@lightbeamai/design-system/dist/style.css'
import {
  FILTER_BY,
  FILTER_SENSITIVITY_MULTIPLE,
  FILTER_SENSITIVITY_VALUES,
  IS_UNRESOLVED,
  URL_ABOUT,
  URL_ACCESS_CONTROL,
  URL_ACCESS_REVIEW,
  URL_ALERTS,
  URL_ATTRIBUTES,
  URL_CONSENT_MANAGEMENT,
  URL_COOKIES_CONSENT,
  URL_DASHBOARD,
  URL_DATA_SOURCES,
  URL_DEFINED_PROCESSES,
  URL_DOCUMENTS_ALL,
  URL_DOCUMENTS_CLASSIFIED,
  URL_DSR,
  URL_DSR_REQUEST_MANAGEMENT,
  URL_ENTITIES,
  URL_INSIGHTS,
  URL_LABEL_MANAGEMENT,
  URL_OVERVIEW,
  URL_PIA,
  URL_POLICIES,
  URL_PRIVACY_OPS,
  URL_PRIVACY_PARTNERS,
  URL_REPORTS,
  URL_ROPA,
  URL_RULE_SET,
  URL_SETTINGS,
  URL_TEMPLATES
} from '../../constants'
import {
  useThemeContext,
  DARK_THEME,
  LIGHT_THEME
} from '../../features/dashboards/mainDashboardv2/components/ThemeContext'
import { UserDetails } from '../../interfaces'
import IconBell from '../../assets/inlineSvg/icon-bell-notifications.svg'
import LightBeamWhiteLogo from '../../assets/images/lightbeam-logo-white.png'
import NotificationsWidget from '../../features/notifications/widgetNotifications'
import GlobalSearch from '../../features/globalSearch/globalSearchNavbar'
import WidgetHelp from '../widgetHelp/help'
import { FeatureFlags } from '../../configs/featureFlagSlice'
import { getUpdatedUrl } from '../../utils/urlUtil'
import { usePermission } from '../../HOCs/PermissionProvider'
import { ACTIONS } from '../PermissionMapper/constants'
import ModalSelectedRoleDefinition from '../../features/users/components/modalAddNewUser/modalSelectedRoleDefinition'
import { AlertFilterByValues } from '../../features/policies/policiesSlice'
import { TopNavMenuItem, TopNav, Button, Icon, Typography } from '@lightbeamai/design-system'
import { NavLink, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Dropdown } from 'semantic-ui-react'
import { connect } from 'react-redux'
import UserAvatar from 'react-user-avatar'
import React, { useEffect, useMemo, useState } from 'react'
import '../../features/menuRight/menuRight.less'
interface IProps {
  userDetails?: UserDetails
  featureFlags: FeatureFlags
}

const Navbar = (props: IProps): React.ReactElement => {
  const { userDetails, featureFlags } = props
  const intl = useIntl()
  const { theme, applyTheme } = useThemeContext()
  const { hasNavAction } = usePermission()
  const [selectedKey, setSelectedKey] = useState<string>('')
  const location = useLocation()

  // Set the theme based on the query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const theme = searchParams.get('theme')

    if (theme) {
      applyTheme(theme)
    } else {
      applyTheme(LIGHT_THEME)
    }
  }, [location.search, applyTheme])

  const urlCookieConsent = URL_COOKIES_CONSENT + URL_OVERVIEW
  const showPolicies = hasNavAction(ACTIONS.POLICIES_VIEW)
  const showMyAlerts = !hasNavAction(ACTIONS.POLICIES_VIEW) && hasNavAction(ACTIONS.MY_ALERTS_VIEW)

  const menuItems: TopNavMenuItem[] = useMemo(() => {
    const myAlerts = showMyAlerts
      ? [
          {
            key: URL_POLICIES + URL_ALERTS,
            to: getUpdatedUrl({
              pathName: URL_POLICIES + URL_ALERTS,
              pageName: URL_ALERTS,
              pageQueryParams: { [IS_UNRESOLVED]: true },
              queryParams: { [FILTER_BY]: AlertFilterByValues.user },
              resetPageQuery: true,
              resetQuery: true
            }),
            label: intl.formatMessage({ id: 'menu.main.alerts' })
          }
        ]
      : []
    const dashboardNav = hasNavAction(ACTIONS.DASHBOARD_VIEW)
      ? [
          {
            key: URL_DASHBOARD,
            to: URL_DASHBOARD,
            label: intl.formatMessage({ id: 'menu.main.dashboard' })
          }
        ]
      : []
    const dataSourcesNav = hasNavAction(ACTIONS.DATA_SOURCE_VIEW)
      ? [
          {
            key: URL_DATA_SOURCES,
            to: URL_DATA_SOURCES,
            label: intl.formatMessage({ id: 'menu.main.dataSources' })
          }
        ]
      : []

    //Playbooks options
    const policies = hasNavAction(ACTIONS.POLICIES_VIEW)
      ? [
          {
            key: URL_RULE_SET,
            to: URL_POLICIES + URL_RULE_SET,
            label: intl.formatMessage({ id: 'menu.main.policies' })
          }
        ]
      : []
    const alerts = hasNavAction(ACTIONS.ALERTS_VIEW)
      ? [
          {
            key: URL_ALERTS,
            to: getUpdatedUrl({
              pathName: URL_POLICIES + URL_ALERTS,
              pageName: URL_ALERTS,
              pageQueryParams: { [IS_UNRESOLVED]: true },
              queryParams: { [FILTER_BY]: AlertFilterByValues.all },
              resetPageQuery: true,
              resetQuery: true
            }),
            label: intl.formatMessage({ id: 'menu.main.alerts' })
          }
        ]
      : []
    const ruleSetOptions = [...policies, ...alerts]

    // insights options
    const attributes = hasNavAction(ACTIONS.ATTRIBUTES_VIEW)
      ? [
          {
            key: URL_ATTRIBUTES,
            to: getUpdatedUrl({
              pathName: URL_INSIGHTS + URL_ATTRIBUTES,
              pageName: URL_ATTRIBUTES,
              pageQueryParams: {
                [FILTER_SENSITIVITY_MULTIPLE]: FILTER_SENSITIVITY_VALUES.high
              }
            }),
            label: intl.formatMessage({ id: 'menu.filters.attributes' }),
            capitalizeLabel: false
          }
        ]
      : []

    const files = hasNavAction(ACTIONS.OBJECTS_VIEW)
      ? [
          {
            key: URL_DOCUMENTS_ALL,
            to: URL_INSIGHTS + URL_DOCUMENTS_ALL,
            label: intl.formatMessage({ id: 'menu.filters.objects' }),
            capitalizeLabel: false
          }
        ]
      : []

    const labels = hasNavAction(ACTIONS.LABELS_VIEW)
      ? [
          {
            key: URL_LABEL_MANAGEMENT,
            to: URL_INSIGHTS + URL_LABEL_MANAGEMENT,
            label: intl.formatMessage({ id: 'menu.filters.labelManagement' }),
            capitalizeLabel: false
          }
        ]
      : []

    const classifications = hasNavAction(ACTIONS.CLASSIFICATIONS_VIEW)
      ? [
          {
            key: URL_DOCUMENTS_CLASSIFIED,
            to: URL_INSIGHTS + URL_DOCUMENTS_CLASSIFIED,
            label: intl.formatMessage({ id: 'documents' }),
            capitalizeLabel: false
          }
        ]
      : []

    const templates = hasNavAction(ACTIONS.TEMPLATES_VIEW)
      ? [
          {
            key: URL_TEMPLATES,
            to: URL_INSIGHTS + URL_TEMPLATES,
            label: intl.formatMessage({ id: 'menu.filters.templates' }),
            capitalizeLabel: false
          }
        ]
      : []

    const entities = hasNavAction(ACTIONS.ENTITIES_VIEW)
      ? [
          {
            key: URL_ENTITIES,
            to: URL_INSIGHTS + URL_ENTITIES,
            label: intl.formatMessage({ id: 'menu.filters.entities' }),
            capitalizeLabel: false
          }
        ]
      : []

    const reports = hasNavAction(ACTIONS.REPORTS_VIEW)
      ? [
          {
            key: URL_REPORTS,
            to: URL_INSIGHTS + URL_REPORTS,
            label: intl.formatMessage({ id: 'menu.filters.reports' }),
            capitalizeLabel: false
          }
        ]
      : []

    const insightsOptions = [
      ...attributes,
      ...files,
      ...labels,
      ...classifications,
      ...templates,
      ...entities,
      ...reports
    ]

    // privacy ops options
    const privacyOpsOverview = hasNavAction(ACTIONS.DSR_VIEW)
      ? [
          {
            key: URL_PRIVACY_OPS + URL_OVERVIEW,
            to: URL_PRIVACY_OPS + URL_OVERVIEW,
            label: intl.formatMessage({ id: 'menu.filters.overview' }),
            capitalizeLabel: false
          }
        ]
      : []
    const dsr = hasNavAction(ACTIONS.DSR_VIEW)
      ? [
          {
            key: URL_DSR + URL_DSR_REQUEST_MANAGEMENT,
            to: URL_DSR + URL_DSR_REQUEST_MANAGEMENT,
            label: intl.formatMessage({ id: 'menu.filters.dsr' }),
            capitalizeLabel: false
          }
        ]
      : []
    const pia =
      !!featureFlags?.piaFeature && hasNavAction(ACTIONS.PIA_VIEW)
        ? [
            {
              key: URL_PIA,
              to: URL_PIA + URL_DEFINED_PROCESSES,
              label: intl.formatMessage({ id: 'menu.filters.pia' }),
              capitalizeLabel: false
            }
          ]
        : []
    const cookieConsent =
      !!featureFlags?.cookieConsent && hasNavAction(ACTIONS.COOKIE_CONSENT_VIEW)
        ? [
            {
              key: urlCookieConsent,
              to: urlCookieConsent,
              label: intl.formatMessage({ id: 'menu.filters.cookieConsent' })
            }
          ]
        : []
    const ropa = hasNavAction(ACTIONS.ROPA_VIEW)
      ? [
          {
            key: URL_ROPA,
            to: URL_ROPA + URL_DEFINED_PROCESSES,
            label: intl.formatMessage({ id: 'ropa.menuItem' }),
            capitalizeLabel: false
          }
        ]
      : []
    const consentManagement = hasNavAction(ACTIONS.CONSENT_MANAGEMENT_VIEW)
      ? [
          {
            key: URL_CONSENT_MANAGEMENT + URL_OVERVIEW,
            to: URL_CONSENT_MANAGEMENT + URL_OVERVIEW,
            label: intl.formatMessage({ id: 'menu.filters.consentManagement' })
          }
        ]
      : []
    const privacyPartners = hasNavAction(ACTIONS.PRIVACY_PARTNERS)
      ? [
          {
            key: URL_PRIVACY_PARTNERS,
            to: URL_PRIVACY_PARTNERS,
            label: intl.formatMessage({ id: 'menu.main.privacyPartners' })
          }
        ]
      : []

    const privacyOpsOptions = [
      ...privacyOpsOverview,
      ...dsr,
      ...consentManagement,
      ...cookieConsent,
      ...pia,
      ...ropa,
      ...privacyPartners
    ]

    // access control options
    const hasGovernanceAccess =
      !!featureFlags.accessControl && hasNavAction(ACTIONS.GOVERNANCE_VIEW)
    const accessControl =
      hasGovernanceAccess && hasNavAction(ACTIONS.ACCESS_CONTROL_VIEW)
        ? [
            {
              key: URL_ACCESS_CONTROL,
              to: URL_ACCESS_CONTROL,
              label: intl.formatMessage({ id: 'menu.main.accessGovernance' })
            }
          ]
        : []
    const accessReview =
      hasGovernanceAccess && hasNavAction(ACTIONS.ACCESS_REVIEW_VIEW)
        ? [
            {
              key: URL_ACCESS_REVIEW,
              to: URL_ACCESS_CONTROL + URL_ACCESS_REVIEW,
              label: intl.formatMessage({ id: 'menu.main.accessReview' })
            }
          ]
        : []

    const accessControlOptions = [...accessControl, ...accessReview]

    const fullOptions = [
      ...dashboardNav,
      ...dataSourcesNav,
      ...(accessControlOptions.length
        ? [
            {
              key: URL_ACCESS_CONTROL,
              to: URL_ACCESS_CONTROL,
              label: intl.formatMessage({ id: 'menu.main.accessControl' }),
              children: accessControlOptions
            }
          ]
        : []),
      ...(privacyOpsOptions.length
        ? [
            {
              key: URL_ROPA,
              to: URL_ROPA,
              label: intl.formatMessage({ id: 'menu.main.privacyOps' }),
              children: privacyOpsOptions
            }
          ]
        : []),
      ...(insightsOptions.length
        ? [
            {
              key: URL_INSIGHTS,
              to: URL_INSIGHTS + URL_ENTITIES,
              label: intl.formatMessage({ id: 'menu.main.insights' }),
              children: insightsOptions
            }
          ]
        : []),
      ...(showPolicies && ruleSetOptions.length
        ? [
            {
              key: URL_POLICIES,
              to: URL_POLICIES + URL_RULE_SET,
              label: intl.formatMessage({ id: 'menu.main.playbooks' }),
              children: ruleSetOptions
            }
          ]
        : []),
      ...myAlerts
    ]

    return fullOptions
  }, [featureFlags, intl, urlCookieConsent, hasNavAction])

  const { firstName, lastName, emailId, roles } = userDetails || {}
  const name = firstName + ' ' + (lastName ?? '')
  const userInitials = firstName?.[0] || '' + lastName?.[0] || ''
  const { name: roleIdName, displayName: roleName } = roles?.[0] || {}

  const profileImg = name ? (
    <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
      <UserAvatar size="30" name={name.substring(0, 2)} imageText={userInitials} />
    </button>
  ) : (
    <></>
  )

  useEffect(() => {
    const currentSelected = findSelectedKey(menuItems, location.pathname)

    if (currentSelected) {
      setSelectedKey(currentSelected.key)
    } else {
      setSelectedKey('')
    }
  }, [location.pathname, menuItems])

  const ProfileAvatar = ({ roleName, roleIdName, emailId }) => {
    /** Commented for LA-11217 */
    // const setThemeQueryParams = (newTheme: string) => {
    //   const searchParams = new URLSearchParams(location.search)

    //   searchParams.set('theme', newTheme)

    //   history.push({
    //     search: searchParams.toString()
    //   })
    // }
    const [showRoleDefinitionModal, setShowRoleDefinitionModal] = useState(false)

    return (
      <div className="user-profile-avatar">
        <Dropdown.Menu>
          <Dropdown.Header className="xs-py-16 xs-px-24 xs-m-0">
            <p className="xs-mb-12">
              <FormattedMessage id="navbar.menu.option.account" />
            </p>
            <div className="header-content">
              {profileImg}
              <div className="xs-ml-12">
                <Typography variant={'base'} className="user-email xs-mb-0">
                  {emailId}
                </Typography>
                <Typography variant={'baseMinus2'} className="role-name xs-mb-0">
                  {roleName}
                </Typography>
                <Button
                  variant="link"
                  icon={<Icon name="LinkExternal01" width={14} height={14} />}
                  iconPosition="right"
                  className="xs-p-0 view-role-info-link"
                  onClick={() => setShowRoleDefinitionModal(true)}
                >
                  <FormattedMessage id="settings.users.addNewUser.viewRoleDefinition" />
                </Button>
                <ModalSelectedRoleDefinition
                  isOpened={showRoleDefinitionModal}
                  selectedRoleName={roleIdName}
                  onClose={() => setShowRoleDefinitionModal(false)}
                />
              </div>
            </div>
          </Dropdown.Header>
          {/* <Dropdown.Item className="cursor-pointer">
            <Tabs
              items={[
                {
                  key: 'light-theme',
                  label: 'Light',
                  icon: <LightBeamIcon style={{ width: '12px' }} />
                },
                {
                  key: 'dark-theme',
                  label: 'Dark',
                  icon: <DarkModeIcon style={{ width: '12px' }} />
                }
              ]}
              variant="button"
              value={theme}
              onChange={setThemeQueryParams}
            ></Tabs>
          </Dropdown.Item> */}
          <Dropdown.Item to="/manage-account" as={NavLink}>
            <p className="user-name xs-mb-4">
              <FormattedMessage id="navbar.menu.option.manage-account" />
            </p>
          </Dropdown.Item>
          <Dropdown.Item onClick={apiService.logout}>
            <FormattedMessage id="navbar.menu.option.logout" />
          </Dropdown.Item>
        </Dropdown.Menu>
      </div>
    )
  }

  const lightBeamDarkMoreLogo = theme === DARK_THEME ? LightBeamWhiteLogo : undefined
  return (
    <header className="lb-navbar" data-test-id="navbar">
      <TopNav
        logo={lightBeamDarkMoreLogo}
        logoLink="/dashboard"
        menuConfig={menuItems}
        showSearch={false}
        showSetting={hasNavAction(ACTIONS.SETTINGS_VIEW)}
        onSettingClick={() => window.location.replace(URL_SETTINGS + URL_ABOUT)}
        menuIcons={[
          <ProtectedContent key={1} actions={[ACTIONS.GLOBAL_SEARCH]}>
            <GlobalSearch />
          </ProtectedContent>,
          <NotificationsWidget key="notification-icon" text={<IconBell />} />,
          <WidgetHelp key={3} />
        ]}
        user={{
          name,
          children: <ProfileAvatar roleName={roleName} roleIdName={roleIdName} emailId={emailId} />
        }}
        selectedKey={selectedKey}
        setSelectedKey={setSelectedKey}
        overflowedIndicatorText={intl.formatMessage({ id: 'menu.main.overflowed' })}
      />
    </header>
  )
}

const mapStateToProps = (state: RootState) => ({
  userDetails: state.user.userInfo,
  featureFlags: state.featureFlags
})

export default connect(mapStateToProps)(Navbar)

// export default Navbar
